import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, CalendarClock, CheckCircle2, AlertTriangle, RefreshCw, PlayCircle, Loader2 } from "lucide-react";

function formatRome(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Rome" });
  } catch { return iso; }
}

const JOB_LABELS = {
  daily_run: "Generazione task giornaliere (camere + HK + FO)",
  audits_daily_run: "Audit pianificati (ciclo 60gg)",
  evaluate_alerts_daily: "Valutazione regole alert",
  hourly_safety_net: "Safety-net orario (catch-up task mancanti)",
};

export default function SchedulerStatus() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/scheduler/status");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runToday = async () => {
    setRunning(true);
    try {
      const { data: res } = await api.post("/admin/scheduler/run-today");
      toast.success(`Task camere generate: ${res.created ?? 0} · scadute marcate: ${res.marked_overdue ?? 0} · suggested ripristinate: ${res.backfilled_suggested ?? 0}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const t = data?.today;
  const lastDaily = data?.last_runs?.daily_run;
  const coverageOk = t?.coverage_ok && t?.camere_with_rooms_suggested >= (t?.active_hotels || 0);

  return (
    <div className="space-y-5" data-testid="scheduler-status-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display tracking-tight">Scheduler</h1>
          <p className="text-sm text-muted-foreground mt-1">Diagnostica dei job automatici (timezone Europe/Rome) e recovery manuale.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="scheduler-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
          <Button onClick={runToday} disabled={running} className="gap-1.5" data-testid="scheduler-run-today">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
            Genera task camere oggi adesso
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <Activity className="h-3 w-3" /> Stato
          </div>
          <div className="flex items-center gap-2">
            {data?.started ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-600 font-medium">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Attivo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-rose-600 font-medium">
                <span className="h-2 w-2 rounded-full bg-rose-500" /> Inattivo
              </span>
            )}
          </div>
          <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
            <div>Instance: <span className="font-mono">{data?.instance_id || "—"}</span></div>
            <div>Avviato: {formatRome(data?.started_at)}</div>
            <div>Ora Roma: {formatRome(data?.rome_now)}</div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2 flex items-center gap-1.5">
            <CalendarClock className="h-3 w-3" /> Copertura camere oggi
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold tabular-nums">{t?.camere_checks ?? 0}</span>
            <span className="text-sm text-muted-foreground">/ {t?.active_hotels ?? 0} hotel</span>
          </div>
          <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
            <div>Data: {t?.date_rome || "—"}</div>
            <div>Con camere suggerite: <span className="font-medium text-foreground">{t?.camere_with_rooms_suggested ?? 0}</span></div>
          </div>
          <div className="mt-3">
            {coverageOk ? (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
                <CheckCircle2 className="h-3 w-3" /> Copertura completa
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
                <AlertTriangle className="h-3 w-3" /> Copertura incompleta — usa "Genera adesso"
              </span>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Ultima esecuzione daily_run</div>
          {lastDaily ? (
            <>
              <div className="text-sm font-medium">{formatRome(lastDaily.ts)}</div>
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                <div>Esito: {lastDaily.ok ? <span className="text-emerald-600">OK</span> : <span className="text-rose-600">errore</span>}</div>
                {lastDaily.result && (
                  <>
                    <div>Task create: <span className="font-medium text-foreground">{lastDaily.result.created ?? 0}</span></div>
                    <div>Scadute marcate: <span className="font-medium text-foreground">{lastDaily.result.marked_overdue ?? 0}</span></div>
                    <div>Suggerite ripristinate: <span className="font-medium text-foreground">{lastDaily.result.backfilled_suggested ?? 0}</span></div>
                  </>
                )}
                {lastDaily.error && <div className="text-rose-600 break-all">err: {lastDaily.error}</div>}
              </div>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Nessun run registrato</div>
          )}
        </Card>
      </div>

      <Card className="p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Jobs pianificati</div>
        <div className="divide-y divide-border/60">
          {(data?.jobs || []).map((j) => (
            <div key={j.id} className="flex items-center justify-between py-3" data-testid={`job-row-${j.id}`}>
              <div>
                <div className="text-sm font-medium">{JOB_LABELS[j.id] || j.id}</div>
                <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{j.id}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Prossimo run</div>
                <div className="text-sm tabular-nums">{formatRome(j.next_run_rome)}</div>
              </div>
            </div>
          ))}
          {(!data?.jobs || data.jobs.length === 0) && (
            <div className="py-3 text-sm text-muted-foreground">Nessun job pianificato</div>
          )}
        </div>
      </Card>

      <p className="text-xs text-muted-foreground">
        Lo scheduler genera automaticamente i Controlli Camere ogni mattina alle 06:00 (Europe/Rome). Un job di safety-net ogni ora compensa eventuali deploy avvenuti dopo le 06:00. In caso di anomalia (es. coverage incompleta), usa <span className="font-medium">Genera task camere oggi adesso</span>.
      </p>
    </div>
  );
}
